import React, { useState, useRef } from 'react';

const PFPGenerator = () => {
  const [uploadedImageBase64, setUploadedImageBase64] = useState(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const uploadInputRef = useRef(null);

  const API_URL = '/api/generate-pfp';
  const STYLE_PROMPT = 'Convert this image into a hand-drawn black-and-white sketch style, fully only using black and white colors. Use thick black ink lines, simple cartoonish facial features, and minimal shading. The drawing should look like it was made with a pen on paper, with rough texture and expressive outlines. Keep the proportions slightly exaggerated but recognizable. The output should be fully black and white color only, no other colors should be in the edited version.';

  const handleUploadClick = () => {
    uploadInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImageBase64(event.target.result);
      setGeneratedImageUrl(null);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!uploadedImageBase64) {
      setError('Please upload an image first!');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageData: uploadedImageBase64,
          prompt: STYLE_PROMPT
        })
      });

      // Check if response is HTML (API not available)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('API endpoint not available. Please deploy to Vercel or set up a backend server.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Server error' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.imageUrl) {
        setGeneratedImageUrl(data.imageUrl);
      } else {
        throw new Error('No image returned from API');
      }
    } catch (err) {
      console.error('PFP Generation Error:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!generatedImageUrl) return;

    try {
      const response = await fetch(generatedImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'Lindy-pfp.png';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download image');
    }
  };

  return (
    <div className="pfp-generator-container" id="pfp-generator-container">
      <h2>PFP</h2>
      <div className="pfp-controls">
        <input
          type="file"
          ref={uploadInputRef}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button onClick={handleUploadClick} className="pfp-btn">Upload Image</button>
      </div>
      {uploadedImageBase64 && (
        <div className="pfp-preview-container">
          <img src={uploadedImageBase64} alt="Image Preview" />
        </div>
      )}
      <div className="pfp-display-area">
        {isGenerating ? (
          <p className="pfp-placeholder">Transforming your image... Please wait...</p>
        ) : error ? (
          <p className="pfp-placeholder" style={{ color: '#ff4444' }}>{error}</p>
        ) : generatedImageUrl ? (
          <img src={generatedImageUrl} alt="Generated" />
        ) : (
          <p className="pfp-placeholder">Generated image will appear here</p>
        )}
      </div>
      <div className="pfp-action-buttons">
        {uploadedImageBase64 && (
          <button
            onClick={handleGenerate}
            className="pfp-btn"
            disabled={isGenerating}
          >
            {isGenerating ? 'Transforming...' : 'Transform'}
          </button>
        )}
        {generatedImageUrl && (
          <button onClick={handleDownload} className="pfp-btn">
            Download Image
          </button>
        )}
      </div>
    </div>
  );
};

export default PFPGenerator;
